import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Calendar, BookOpen } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import type { Update } from '@/lib/types';

export function ArticleCard({ article }: { article: Update }) {
  const href = `/community/${article.slug || article._id}`;

  return (
    <Link href={href} className="block group">
      <Card className="border-border shadow-soft bg-card rounded-[2.5rem] overflow-hidden hover:shadow-soft-hover transition-all duration-700 h-full flex flex-col">
        <CardHeader className="p-0">
          <div className="aspect-[16/10] relative overflow-hidden bg-secondary/30">
            {article.coverImage ? (
              <Image 
                src={article.coverImage} 
                alt={article.title} 
                fill 
                className="object-cover group-hover:scale-105 transition-transform duration-1000" 
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-primary/5">
                  <BookOpen className="w-12 h-12 text-primary/10" />
              </div>
            )}
            <div className="absolute top-6 left-6 flex gap-2">
              <Badge className="bg-card/90 backdrop-blur-md text-primary px-4 py-1.5 rounded-full font-black text-[9px] uppercase tracking-widest border-none">
                {article.type === 'announcement' ? 'Announcement' : 'Article'}
              </Badge>
              {article.category && (
                <Badge className="bg-accent/90 backdrop-blur-md text-accent-foreground px-4 py-1.5 rounded-full font-black text-[9px] uppercase tracking-widest border-none">
                  {article.category}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-8 flex-grow flex flex-col space-y-4">
          <div className="flex items-center gap-2 text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">
            <Calendar className="w-3 h-3" />
            <span>{new Date(article.publishedAt || article.createdAt).toLocaleDateString()}</span>
          </div>

          <h3 className="text-2xl font-headline font-black text-foreground leading-tight group-hover:text-primary transition-colors">
            {article.title}
          </h3>
          
          <p className="text-sm text-muted-foreground font-medium line-clamp-3 flex-grow leading-relaxed">
            {article.excerpt || article.content}
          </p>

          <div className="pt-4 flex items-center justify-between border-t border-border/50 mt-2">
            <div className="flex items-center gap-2">
               {article.authorImage ? (
                  <div className="relative w-6 h-6 rounded-full overflow-hidden">
                      <Image 
                        src={article.authorImage} 
                        alt={article.authorName || 'Author'} 
                        fill 
                        className="object-cover"
                        style={{
                            transform: article.authorPhotoConfig 
                                ? `scale(${article.authorPhotoConfig.scale}) translate(${article.authorPhotoConfig.x}%, ${article.authorPhotoConfig.y}%)`
                                : undefined
                        }}
                      />
                  </div>
               ) : (
                  <div className="w-6 h-6 rounded-full bg-secondary/50 flex items-center justify-center text-[10px] font-bold text-primary">
                    {(article.authorName || 'B').charAt(0)}
                  </div>
               )}
               <span className="text-xs font-bold text-muted-foreground">{article.authorName || article.author || 'Breeder'}</span>
            </div>

            <div className="flex items-center text-primary font-black text-[10px] uppercase tracking-widest gap-2">
              <span>Read</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
